import { createStore } from 'zustand';
import { devtools } from 'zustand/middleware';

import { REPUBS_WITH_BOOKIE_LOGOS, REPUBS_WITH_TRANSLATED_BET_NAMES } from '@cx-modules/constants';

import {
  type OddsGridResponseData,
  type OddsGridStoreProps,
  type OddsUpdate,
  type SelectedBet,
  type SyncBet,
} from './types';

export type Store = OddsGridStoreProps &
  OddsGridResponseData & {
    addSelectedBet: (selectedBet: SelectedBet) => void;
    disableStreamer?: boolean;
    removeAllSelectedBets: () => void;
    removeSelectedBet: (betId: number) => void;
    renderOddsButtonBadge: boolean;
    seeMoreOddsTooltip?: boolean;
    selectedBets: SelectedBet[];
    setSeeMoreOddsTooltip: (seeMoreOddsTooltip: boolean) => void;
    syncBetslip: (bets: SyncBet[], overwriteSelections?: boolean) => void;
    translateBetNames: boolean;
    updatePrice: (betId: number, bookmakerCode: string, update: OddsUpdate) => void;
  };

const transformBets = (b: SyncBet[]): SelectedBet[] =>
  b.map(b => ({
    betId: b.betId,
    bookieCode: b.bestOddsBookmakers,
  }));

export const createOddsGridStore = ({
  data,
  onOddsButtonClick,
  onOfferClick,
  ...storeProps
}: OddsGridStoreProps & {
  data: OddsGridResponseData | null;
}) => {
  const renderOddsButtonBadge = REPUBS_WITH_BOOKIE_LOGOS.includes(storeProps.repub) || !!storeProps.showBookieLogo;
  const translateBetNames = REPUBS_WITH_TRANSLATED_BET_NAMES.includes(storeProps.repub);

  return createStore<Store>()(
    devtools(set => ({
      ...storeProps,
      ...(data as OddsGridResponseData),
      addSelectedBet: (bet: SelectedBet) => {
        set((state: { selectedBets: SelectedBet[] }) => ({
          selectedBets: [...state.selectedBets.filter(b => b.betId !== bet.betId), bet],
        }));
      },
      onOddsButtonClick,
      onOfferClick,
      removeAllSelectedBets: () => {
        set({ selectedBets: [] });
      },
      removeSelectedBet: (id: number) => {
        set((state: { selectedBets: SelectedBet[] }) => ({
          selectedBets: state.selectedBets.filter(b => b.betId !== id),
        }));
      },
      renderOddsButtonBadge,
      selectedBets: [],
      setSeeMoreOddsTooltip: (seeMoreOddsTooltip: boolean) => set({ seeMoreOddsTooltip }),
      syncBetslip: (bets: SyncBet[], overwriteSelections?: boolean) => {
        set((state: { selectedBets?: SelectedBet[] }) => {
          const selectedBets = state.selectedBets || [];

          // overwrite existing selections
          if (overwriteSelections || !selectedBets.length) {
            const overwrittenBets = transformBets(bets);

            return {
              selectedBets: overwrittenBets,
            };
          }

          // combine existing and new selections
          const newBetsWithoutExistingBets = bets.filter(({ betId }) => !selectedBets.some(b => betId === b.betId));
          const existingBetsWithoutNewBets = selectedBets.filter(({ betId }) => !bets.some(b => b.betId === betId));
          const combinedNewAndExistingBets = [
            ...transformBets(newBetsWithoutExistingBets),
            ...existingBetsWithoutNewBets,
          ];

          return {
            selectedBets: combinedNewAndExistingBets,
          };
        });
      },
      translateBetNames,
      updatePrice: (betId: number, bookmakerCode: string, update: OddsUpdate) => {
        set((state: { bets?: OddsGridResponseData['bets'] }) => {
          const bets = state.bets;
          if (!bets) return {};

          const betIndex = bets.findIndex(bet => bet.id === betId);
          if (betIndex === -1) return {};

          const bet = bets[betIndex];
          const oddIndex = bet.odds.findIndex(odd => odd.bookmakerCode === bookmakerCode);
          if (oddIndex === -1) return {};

          const odd = bet.odds[oddIndex];

          // The wire payload carries the direction (SHORTENING/DRIFTING) that
          // drives the colour + flash animation; fall back to the prior value
          // when an update omits it.
          const movement = update.movement ?? odd.movement;

          // Rebuild only the changed odd/bet so every other reference is kept
          // stable and only the affected memoised cell re-renders.
          const newOdds = [...bet.odds];
          newOdds[oddIndex] = { ...odd, ...update, movement };
          const newBets = [...bets];
          newBets[betIndex] = { ...bet, odds: newOdds };

          return { bets: newBets };
        });
      },
    })),
  );
};
